import React, { useMemo, useState } from 'react';
import { Button, Form, Modal, Spinner } from 'react-bootstrap';

export type WaitlistModalProps = {
  show: boolean;
  onHide: () => void;
  presetReason?: 'request-access' | 'design-partner';
};

type FormState = {
  name: string;
  email: string;
  company: string;
  role: string;
  cloud: { publicCloud: boolean; privateCloud: boolean; orchestration: boolean; onprem: boolean };
  deployingAgents: 'yes' | 'no' | null;
  message: string;
};

const initialState: FormState = {
  name: '',
  email: '',
  company: '',
  role: '',
  cloud: { publicCloud: false, privateCloud: false, orchestration: false, onprem: false },
  deployingAgents: null,
  message: ''
};

function isWorkEmail(email: string) {
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return false;
  const freeDomains = ['gmail.com', 'yahoo.com', 'outlook.com', 'hotmail.com', 'icloud.com', 'aol.com', 'protonmail.com'];
  const domain = email.split('@')[1]?.toLowerCase();
  return domain ? !freeDomains.includes(domain) : false;
}

export default function WaitlistModal({ show, onHide, presetReason }: WaitlistModalProps) {
  const [data, setData] = useState<FormState>(initialState);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  React.useEffect(() => {
    if (show) {
      setData((d) => ({ ...d, message: presetReason === 'design-partner' && !d.message ? 'Interested in the Design Partner program.' : d.message }));
      setErrors({});
      setSubmitted(false);
    }
  }, [show, presetReason]);

  const cloudSelected = useMemo(() => Object.values(data.cloud).some(Boolean), [data.cloud]);

  function validate(): boolean {
    const next: Record<string, string> = {};
    if (!data.name.trim()) next.name = 'Name is required';
    if (!isWorkEmail(data.email)) next.email = 'Use a valid work email';
    if (!data.company.trim()) next.company = 'Company is required';
    if (!data.role.trim()) next.role = 'Role is required';
    if (!cloudSelected) next.cloud = 'Select at least one environment';
    if (data.deployingAgents === null) next.deployingAgents = 'Please select Yes or No';
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const res = await fetch('/api/waitlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...data, presetReason })
      });
      if (!res.ok) throw new Error('Request failed');
      setSubmitted(true);
      setData(initialState);
    } catch {
      setErrors({ form: 'Submission failed. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  }

  const title = presetReason === 'design-partner' ? 'Become a Design Partner' : 'Join the Waitlist';

  return (
    <Modal show={show} onHide={onHide} aria-labelledby="waitlist-title" centered data-bs-theme="dark">
      <Modal.Header closeButton>
        <Modal.Title id="waitlist-title">{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {submitted ? (
          <p className="mb-0">Thanks — we’ll follow up shortly.</p>
        ) : (
          <Form onSubmit={onSubmit} noValidate>
            {errors.form && <div className="text-danger mb-2" role="alert">{errors.form}</div>}
            <Form.Group className="mb-3" controlId="wl-name">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                value={data.name}
                onChange={(e) => setData({ ...data, name: e.target.value })}
                isInvalid={!!errors.name}
              />
              <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-3" controlId="wl-email">
              <Form.Label>Work Email</Form.Label>
              <Form.Control
                type="email"
                inputMode="email"
                value={data.email}
                onChange={(e) => setData({ ...data, email: e.target.value })}
                isInvalid={!!errors.email}
              />
              <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-3" controlId="wl-company">
              <Form.Label>Company</Form.Label>
              <Form.Control
                type="text"
                value={data.company}
                onChange={(e) => setData({ ...data, company: e.target.value })}
                isInvalid={!!errors.company}
              />
              <Form.Control.Feedback type="invalid">{errors.company}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-3" controlId="wl-role">
              <Form.Label>Role</Form.Label>
              <Form.Control
                type="text"
                value={data.role}
                onChange={(e) => setData({ ...data, role: e.target.value })}
                isInvalid={!!errors.role}
              />
              <Form.Control.Feedback type="invalid">{errors.role}</Form.Control.Feedback>
            </Form.Group>
            <fieldset className="mb-3">
              <legend className="fs-6">Current environments</legend>
              {errors.cloud && <div className="text-danger mb-2" role="alert">{errors.cloud}</div>}
              <div className="d-flex flex-wrap gap-3">
                <Form.Check
                  id="cloud-public"
                  label="Public cloud"
                  checked={data.cloud.publicCloud}
                  onChange={(e) => setData({ ...data, cloud: { ...data.cloud, publicCloud: e.currentTarget.checked } })}
                />
                <Form.Check
                  id="cloud-private"
                  label="Private cloud"
                  checked={data.cloud.privateCloud}
                  onChange={(e) => setData({ ...data, cloud: { ...data.cloud, privateCloud: e.currentTarget.checked } })}
                />
                <Form.Check
                  id="cloud-orch"
                  label="Container/orchestration platform"
                  checked={data.cloud.orchestration}
                  onChange={(e) => setData({ ...data, cloud: { ...data.cloud, orchestration: e.currentTarget.checked } })}
                />
                <Form.Check
                  id="cloud-onprem"
                  label="On‑prem"
                  checked={data.cloud.onprem}
                  onChange={(e) => setData({ ...data, cloud: { ...data.cloud, onprem: e.currentTarget.checked } })}
                />
              </div>
            </fieldset>
            <fieldset className="mb-3">
              <legend className="fs-6">Are you deploying AI agents today?</legend>
              {errors.deployingAgents && <div className="text-danger mb-2" role="alert">{errors.deployingAgents}</div>}
              <div className="d-flex gap-3">
                <Form.Check
                  id="agents-yes"
                  type="radio"
                  name="deployingAgents"
                  label="Yes"
                  checked={data.deployingAgents === 'yes'}
                  onChange={() => setData({ ...data, deployingAgents: 'yes' })}
                />
                <Form.Check
                  id="agents-no"
                  type="radio"
                  name="deployingAgents"
                  label="No"
                  checked={data.deployingAgents === 'no'}
                  onChange={() => setData({ ...data, deployingAgents: 'no' })}
                />
              </div>
            </fieldset>
            <Form.Group className="mb-3" controlId="wl-message">
              <Form.Label>Message</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={data.message}
                onChange={(e) => setData({ ...data, message: e.target.value })}
              />
            </Form.Group>
            <div className="d-flex justify-content-end gap-2">
              <Button variant="secondary" onClick={onHide} disabled={submitting}>Cancel</Button>
              <Button type="submit" variant="primary" disabled={submitting} aria-label="Submit waitlist">
                {submitting ? <><Spinner size="sm" className="me-2" />Submitting</> : 'Submit'}
              </Button>
            </div>
          </Form>
        )}
      </Modal.Body>
    </Modal>
  );
}
